import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useToast, useDisclosure } from '@chakra-ui/react';

import { useCompaniesOptions } from '../../../../../hooks/useCompaniesOptions';
import MessageApi from '../../../../../api/message';

const convertParamsToObject = (params) => {
  return params.reduce((acc, param, index) => {
    acc[index] = param;
    return acc;
  }, {});
};

const CampaignsContext = createContext();

export const useCampaigns = () => useContext(CampaignsContext);

export const CampaignsProvider = ({ children }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loaders, setLoaders] = useState({
    isTableLoading: false,
    isCreateEditLoading: false,
  });
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [currentEditCampaign, setCurrentEditCampaign] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const toast = useToast();
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { companiesOptions, companiesIdCache } = useCompaniesOptions({ showAll: false });

  const refreshCampaigns = useCallback(async () => {
    try {
      setLoaders((prevLoaders) => ({ ...prevLoaders, isTableLoading: true }));
      const res = await MessageApi.getCampaigns();
      setCampaigns(res.data);
    } catch (e) {
      console.error('Failed to fetch campaigns', e);
      toast({
        title: 'שגיאה בטעינת קמפיינים',
        description: 'אנא נסה שנית מאוחר יותר',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setLoaders((prevLoaders) => ({ ...prevLoaders, isTableLoading: false }));
    }
  }, [toast]);

  const toggleCampaignSelection = (campaign) => {
    setSelectedCampaigns((prevSelected) => {
      const campaignId = campaign.id;
      const isSelected = prevSelected.some((item) => item.id === campaignId);

      if (isSelected) {
        return prevSelected.filter((item) => item.id !== campaignId);
      } else {
        return [...prevSelected, campaign];
      }
    });
  };

  const selectAllCampaigns = () => {
    if (selectedCampaigns.length === campaigns.length) {
      setSelectedCampaigns([]);
    } else {
      setSelectedCampaigns([...campaigns]);
    }
  };

  const handleEditCampaign = (campaign) => {
    try {
      const selectedCompanies = [
        {
          label: companiesIdCache[campaign.companyId] || 'N/A',
          value: String(campaign.companyId || ''),
        },
      ];

      const messagesTransformedForEdit = campaign.messageSequence.messages.map((message) => {
        let processedParams = [];

        if (message.params && typeof message.params === 'object') {
          if (Array.isArray(message.params)) {
            processedParams = [...message.params];
          } else {
            const paramEntries = Object.entries(message.params)
              .filter(([key]) => !isNaN(parseInt(key)))
              .sort((a, b) => parseInt(a[0]) - parseInt(b[0]));

            processedParams = paramEntries.map(([_, value]) => value);
          }
        }

        return {
          ...message,
          channelType: String(message.channelType),
          daysAfterPrevious: Number(message.daysAfterPrevious || 0),
          params: processedParams,
          mediaUrl: message.mediaUrl || '',
        };
      });

      const campaignTransformedForEdit = {
        ...campaign,
        companies: selectedCompanies,
        messageSequence: {
          messages: messagesTransformedForEdit,
        },
      };

      setCurrentEditCampaign(campaignTransformedForEdit);
      setIsEditMode(true);
      onModalOpen();
    } catch (error) {
      console.error('Error formatting campaign for edit:', error);
      toast({
        title: 'שגיאה בעת ניסיון עריכת קמפיין',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const handleCreateModalOpen = () => {
    setCurrentEditCampaign(null);
    setIsEditMode(false);
    onModalOpen();
  };

  const handleError = (error) => {
    console.error('Failed: ', error);

    const serverMessage = error?.response?.data?.message;

    toast({
      title: 'שגיאה',
      description:
        typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : 'אנא נסה שנית מאוחר יותר',
      status: 'error',
      duration: 5000,
      isClosable: true,
      position: 'top',
    });
  };

  const handleSuccess = () => {
    onModalClose();
    setSelectedCampaigns([]);
    setCurrentEditCampaign(null);
    toast({
      title: 'הפעולה התבצעה בהצלחה',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
    refreshCampaigns();
  };

  const handleSubmit = async (data) => {
    try {
      setLoaders((prevLoaders) => ({ ...prevLoaders, isCreateEditLoading: true }));
      const companyIds = data.companies.map((company) => Number(company.value));

      const payload = {
        type: data.type,
        companyIds,
        isActive: data.isActive,
        messageSequence: {
          messages: data.messageSequence.messages.map((message, index) => ({
            sequenceStep: index + 1,
            templateKey: message.templateKey,
            daysAfterPrevious: Number(message.daysAfterPrevious || 0),
            channelType: Number(message.channelType),
            params: convertParamsToObject(message.params),
            // Omitted entirely when empty: the server rejects a mediaUrl on a template
            // whose header carries no media
            ...(message.mediaUrl ? { mediaUrl: message.mediaUrl } : {}),
          })),
        },
      };

      if (isEditMode) {
        await MessageApi.updateCampaigns(payload);
      } else {
        await MessageApi.createCampaigns(payload);
      }

      handleSuccess();
    } catch (error) {
      handleError(error);
    } finally {
      setLoaders((prevLoaders) => ({ ...prevLoaders, isCreateEditLoading: false }));
    }
  };

  const handleDeleteCampaigns = async () => {
    setLoaders((prevLoaders) => ({ ...prevLoaders, isCreateEditLoading: true }));
    try {
      const campaignIds = selectedCampaigns.map((campaign) => campaign.id);
      await MessageApi.deleteCampaigns(campaignIds);
      handleSuccess();
    } catch (error) {
      handleError(error);
    } finally {
      setLoaders((prevLoaders) => ({ ...prevLoaders, isCreateEditLoading: false }));
    }
  };

  useEffect(() => {
    refreshCampaigns();
  }, [refreshCampaigns]);

  const value = {
    campaigns,
    companiesOptions,
    companiesIdCache,
    selectedCampaigns,
    currentEditCampaign,
    loaders,
    isEditMode,
    isModalOpen,
    onModalClose,
    handleCreateModalOpen,
    handleDeleteCampaigns,
    handleEditCampaign,
    handleSubmit,
    toggleCampaignSelection,
    selectAllCampaigns,
  };

  return <CampaignsContext.Provider value={value}>{children}</CampaignsContext.Provider>;
};
